import {
  InvoiceWithPostings,
  Posting,
  PostingType,
  PostingWithAmountSelected,
  Transaction,
  TransactionLinkType,
  TransactionStatus,
  TransactionWithAmountLinked,
  filterAndMap,
  getPostingAmountFromInvoice,
  getPostingsWithAmountLeftToPay,
  getTransactionAmountReconciledInLeaseOrContractor,
  isNegativeAmountInvoice,
  isPostingARentDiscount,
  isPostingARentPayment,
  roundAtSecondDecimal,
} from '@rentguru/commons-utils';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';

export enum ReconciliationType {
  TRANSACTION_AND_INVOICES,
  TRANSACTION_AND_INVOICES_WITH_PENDING_BALANCE,
  TRANSACTION_AND_TRANSACTION,
  PENDING_BALANCE_AND_INVOICES,
  INVOICES,
  NONE,
}

export const getReconciliationType = (
  postingsSelected: PostingWithAmountSelected[],
  positiveTransactionSelected: TransactionWithAmountLinked | null,
  negativeTransactionSelected: TransactionWithAmountLinked | null,
  invoicesToReconcile: InvoiceWithPostings[],
  pendingBalance: {
    amount: number;
    isChecked: boolean;
  },
  takePendingBalanceIntoAccount: boolean
): ReconciliationType => {
  if (
    isEmpty(postingsSelected) &&
    positiveTransactionSelected &&
    negativeTransactionSelected &&
    Math.abs(positiveTransactionSelected.amountToBeReconciled) ===
      Math.abs(negativeTransactionSelected.amountToBeReconciled)
  ) {
    // One positive transaction with one negative transaction EXACT match
    return ReconciliationType.TRANSACTION_AND_TRANSACTION;
  }
  const amountLeftToAssign = getAmountOfSelection(
    postingsSelected,
    positiveTransactionSelected,
    negativeTransactionSelected
  );

  if (!positiveTransactionSelected && !negativeTransactionSelected && !isEmpty(postingsSelected)) {
    if (pendingBalance.isChecked && pendingBalance.amount !== 0) {
      return ReconciliationType.PENDING_BALANCE_AND_INVOICES;
    }
    if (amountLeftToAssign === 0) {
      // Bundle of invoices and credit note
      return ReconciliationType.INVOICES;
    }
  }
  const oneTransactionSelected =
    (positiveTransactionSelected && !negativeTransactionSelected) ||
    (!positiveTransactionSelected && negativeTransactionSelected);

  if (oneTransactionSelected && !isEmpty(postingsSelected)) {
    if (amountLeftToAssign !== 0) {
      if (takePendingBalanceIntoAccount) return ReconciliationType.TRANSACTION_AND_INVOICES_WITH_PENDING_BALANCE;
      if (!takePendingBalanceIntoAccount) return ReconciliationType.NONE;
    }
    if (amountLeftToAssign === 0) {
      // One transaction is paying for multiple invoices and exact match
      return ReconciliationType.TRANSACTION_AND_INVOICES;
    }
  }
  const allPostingsAreSelected = areAllPostingsSelected(invoicesToReconcile, postingsSelected);
  if (oneTransactionSelected && !isEmpty(postingsSelected) && allPostingsAreSelected) {
    // One transaction is paying for multiple invoices and no exact match because we have no invoices left to pay
    const amountLeftToAssignLowerThanTransaction =
      (positiveTransactionSelected &&
        amountLeftToAssign > 0 &&
        amountLeftToAssign < positiveTransactionSelected.amountToBeReconciled) ||
      (negativeTransactionSelected &&
        amountLeftToAssign < 0 &&
        amountLeftToAssign > negativeTransactionSelected.amountToBeReconciled);
    // We allow this exceptional case, only if the new transaction
    // that will be created resulting this reconciliation will
    // be a reduced version of the initial one. This means that it should not change of sign and have a lower amount.
    // Example 1: transaction of +200 can become a new transaction of ]0, 199]
    // example 2: transaction of -500 can become a new transaction of [-499, 0[
    if (amountLeftToAssignLowerThanTransaction) return ReconciliationType.TRANSACTION_AND_INVOICES;
  }
  return ReconciliationType.NONE;
};

const areAllPostingsSelected = (
  invoicesToReconcile: InvoiceWithPostings[],
  postingsSelected: PostingWithAmountSelected[]
) => {
  // true if we can't find an invoice that is not selected
  return (
    !isEmpty(invoicesToReconcile) &&
    !invoicesToReconcile.some((invoice) => !isInvoiceSelected(invoice, postingsSelected))
  );
};

export const getAmountOfSelection = (
  postingsSelected: PostingWithAmountSelected[],
  positiveTransactionSelected?: TransactionWithAmountLinked | null,
  negativeTransactionSelected?: TransactionWithAmountLinked | null
) => {
  let amountSelected = 0;
  if (positiveTransactionSelected) amountSelected += positiveTransactionSelected.amountToBeReconciled;
  if (negativeTransactionSelected) amountSelected += negativeTransactionSelected.amountToBeReconciled;
  if (postingsSelected) amountSelected -= sumBy(postingsSelected, 'amountSelected');
  return roundAtSecondDecimal(amountSelected);
};

export const isInvoiceSelected = (invoice: InvoiceWithPostings, postingsSelected: PostingWithAmountSelected[]) => {
  // true if we can't find a posting that is not selected or that is not paid entirely by the selection
  const postingsWithAmountLeftToPay = getPostingsWithAmountLeftToPay(invoice.postings, false);
  return !postingsWithAmountLeftToPay.some((posting) => {
    const postingSelection = postingsSelected.find((postingSelected) => postingSelected.id === posting.id);
    return !(postingSelection && postingSelection.amountSelected === posting.totalAmount);
  });
};

export const isPostingOfNegativeAmountInvoice = (
  posting: Pick<Posting, 'invoice' | 'invoiceId'>,
  invoicesOfLease: InvoiceWithPostings[]
) => {
  if (isNil(posting.invoice)) {
    return false;
  }
  const invoicePostings = invoicesOfLease.find((invoice) => invoice.id === posting.invoiceId)?.postings ?? [];
  return isNegativeAmountInvoice(posting.invoice, invoicePostings);
};

/**
 * If the posting is a rent posting => This will returns every rent reductions postings
 * And vice-versa
 */
export const getLinkedPostings = (
  posting: Posting,
  otherOriginalPostings: Posting[],
  otherPostingsWithAmount: Posting[]
) => {
  let linkedPostings: Posting[] = [];
  if (isPostingARentDiscount(posting) || isPostingARentPayment(posting)) {
    linkedPostings = otherOriginalPostings.filter(
      (p) => p.id !== posting.id && (isPostingARentDiscount(p) || isPostingARentPayment(p))
    );
  }
  return linkedPostings.map((p) => {
    const postingWithAmountSelectable = otherPostingsWithAmount.find((op) => op.id === p.id);
    return {
      originalPosting: p,
      amountSelectable: postingWithAmountSelectable ? postingWithAmountSelectable.totalAmount : 0,
    };
  });
};

/**
 * First: rent discounts
 * Second: rents
 * Third: the rest
 */
const sortByRentPriority = <T extends Posting>(postings: T[]) => {
  return postings.sort((p1, p2) => {
    if (isPostingARentDiscount(p1)) return -1;
    if (isPostingARentDiscount(p2)) return 1;
    if (isPostingARentPayment(p1)) return -1;
    if (isPostingARentPayment(p2)) return 1;
    return -1;
  });
};

export const sortInvoicesByRentPriority = (invoicesWithPostings: InvoiceWithPostings[]) =>
  invoicesWithPostings.map((invoiceWithPosting) => ({
    ...invoiceWithPosting,
    postings: sortByRentPriority(invoiceWithPosting.postings),
  }));

export const sortPostingsByPositivesAndInvoices = (postings: PostingWithAmountSelected[], ascending: boolean) => {
  return postings.sort((p1, p2) => {
    if (ascending) {
      if (p1.amountSelected < 0) return -1;
      if (p2.amountSelected < 0) return 1;
      return p1.invoiceId && p2.invoiceId && p1.invoiceId < p2.invoiceId ? -1 : 1;
    }
    if (p1.amountSelected > 0) return -1;
    if (p2.amountSelected > 0) return 1;
    return p1.invoiceId && p2.invoiceId && p1.invoiceId < p2.invoiceId ? -1 : 1;
  });
};

/**
 * Split the transactions into 2 groups.
 * @returns [group1, group2]
 *
 * group1 = reconciled transactions,
 * group2 = Unreconciled transactions,
 */
export const splitTransactionsIntoReconciliationGroups = (transactions: Transaction[], leaseId: string) => {
  return transactions.reduce(
    (acc: [TransactionWithAmountLinked[], TransactionWithAmountLinked[]], transaction) => {
      if (transaction.status === TransactionStatus.RECONCILED) {
        acc[0].push({ ...transaction, amountToBeReconciled: 0, amountLinked: transaction.amount });
        return acc;
      }
      const splitForLease = transaction.links?.find(
        (t) => t.linkType === TransactionLinkType.LEASE && t.linkId === leaseId
      );
      if (!splitForLease) {
        acc[1].push({ ...transaction, amountLinked: 0, amountToBeReconciled: transaction.amount });
        return acc;
      }
      // The amount to be reconciled for this transaction on this lease
      const transactionAmount = roundAtSecondDecimal(splitForLease.amount);
      if (isNil(transaction.postings) || isEmpty(transaction.postings)) {
        acc[1].push({ ...transaction, amountLinked: 0, amountToBeReconciled: transactionAmount });
        return acc;
      }
      const leaseTransactionPostings = transaction.postings.filter((p) => isNil(p.lease) || p.lease.id === leaseId);
      const amountReconciled = getTransactionAmountReconciledInLeaseOrContractor(transaction, leaseId);
      // The ignored transaction have an amount of 0
      if (amountReconciled === 0 && transaction.status !== TransactionStatus.IGNORE) {
        acc[1].push({ ...transaction, amountToBeReconciled: transactionAmount, amountLinked: 0, postings: [] });
      } else if (amountReconciled !== transactionAmount) {
        // Partially reconciled transaction => split into one reconciled and one not reconciled
        acc[0].push({
          ...transaction,
          amountToBeReconciled: roundAtSecondDecimal(transactionAmount - amountReconciled),
          amountLinked: amountReconciled,
          postings: leaseTransactionPostings,
        }); // Reconciled one
        acc[1].push({
          ...transaction,
          amountToBeReconciled: roundAtSecondDecimal(transactionAmount - amountReconciled),
          amountLinked: amountReconciled,
          postings: [],
        }); // Unreconciled one
      } else {
        acc[0].push({
          ...transaction,
          amountToBeReconciled: roundAtSecondDecimal(transactionAmount - amountReconciled),
          amountLinked: amountReconciled,
          postings: leaseTransactionPostings,
        });
      }

      return acc;
    },
    [[], []]
  );
};

export const getCreditNoteInvoicePostingsForReconciliation = (postings: Posting[]) =>
  postings.reduce<PostingWithAmountSelected[]>(
    filterAndMap({
      filter: (posting) => posting.type === PostingType.DEBIT,
      map: (posting) => ({ ...posting, amountSelected: getPostingAmountFromInvoice(posting, posting.invoice) }),
    }),
    []
  );
